/**
 * Scholarlab integration helpers.
 */

function getApiBaseUrl() {
  const configured = (process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001').trim();
  return configured.replace(/\/$/, '').replace(/\/api$/, '');
}

function getAdminApiKey() {
  const adminApiKey = process.env.REACT_APP_ADMIN_API_KEY;
  if (!adminApiKey) {
    throw new Error('REACT_APP_ADMIN_API_KEY not configured');
  }
  return adminApiKey;
}

export async function launchScholarlabSso(userId, options = {}) {
  try {
    console.info('[Scholarlab] Provider tile clicked', {
      userId,
      openInNewTab: Boolean(options.openInNewTab),
    });

    const relativePath = '/providers/scholarlab';

    if (options.openInNewTab) {
      const userData = sessionStorage.getItem('currentUser');
      if (userData) {
        localStorage.setItem('scholarlab_currentUser', userData);
      }
      const fullUrl = `${window.location.origin}${relativePath}`;
      window.open(fullUrl, '_blank');
    } else if (options.navigate && typeof options.navigate === 'function') {
      options.navigate(relativePath);
    } else {
      window.location.href = relativePath;
    }

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Failed to launch Scholarlab',
      details: error,
    };
  }
}

export async function fetchScholarlabSimulations(userId) {
  console.info('[Scholarlab] Fetching simulations', { userId });

  const response = await fetch(
    `${getApiBaseUrl()}/api/providers/scholarlab/simulations?${new URLSearchParams({ userId })}`,
    {
      method: 'GET',
      headers: {
        'x-api-key': getAdminApiKey(),
      },
    }
  );

  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    console.error('[Scholarlab] Failed to fetch simulations', {
      status: response.status,
      data,
    });
    throw new Error(data.message || data.error || 'Failed to fetch Scholarlab simulations');
  }

  console.info('[Scholarlab] Simulations loaded', {
    userId,
    count: data.simulations?.length || 0,
    scholarlabUserName: data.userName,
  });

  return data;
}

export async function buildScholarlabLaunchUrl(userId, simulation) {
  console.info('[Scholarlab] Requesting launch URL', {
    userId,
    simulationId: simulation?.customUseCaseId || simulation?.id,
    module: simulation?.module,
  });

  const response = await fetch(`${getApiBaseUrl()}/api/providers/scholarlab/launch`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': getAdminApiKey(),
    },
    body: JSON.stringify({
      userId,
      simulation: simulation?.raw || {
        WebGLUrl: simulation?.webglUrl,
        CustomUseCaseId: simulation?.customUseCaseId,
        Module: simulation?.module,
      },
    }),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok || !data.launchUrl) {
    console.error('[Scholarlab] Failed to build launch URL', {
      status: response.status,
      responseKeys: Object.keys(data || {}),
    });
    throw new Error(data.message || data.error || 'Failed to launch Scholarlab simulation');
  }

  console.info('[Scholarlab] Launch URL ready', {
    userId,
    simulationId: simulation?.customUseCaseId || simulation?.id,
    hasLaunchUrl: Boolean(data.launchUrl),
  });

  return data.launchUrl;
}

export function isScholarlabProvider(providerOrKey) {
  const key = typeof providerOrKey === 'string' ? providerOrKey : providerOrKey?.key;
  const name = typeof providerOrKey === 'object' ? providerOrKey?.name : '';

  const normalizedKey = String(key || '').toLowerCase().replace(/[\s_-]+/g, '');
  if (normalizedKey === 'scholarlab') {
    return true;
  }

  return /scholarlab|scholar\s*lab/i.test(name || '');
}

export function getScholarlabErrorMessage(error) {
  const errorLower = (error || '').toLowerCase();

  if (errorLower.includes('api key') || errorLower.includes('unauthorized')) {
    return 'Authentication failed. Please contact support.';
  }

  if (errorLower.includes('token')) {
    return 'Scholarlab session could not be created. Please try again.';
  }

  if (errorLower.includes('network') || errorLower.includes('fetch')) {
    return 'Network error. Please check your connection and try again.';
  }

  return error || 'Failed to launch Scholarlab. Please try again or contact support.';
}
